/**
 * Auth API — wraps /api/auth/* endpoints from app/api/auth.py.
 *
 * Backend contract (source of truth: app/api/auth.py):
 *   POST /api/auth/login    (OAuth2PasswordRequestForm: username/password, x-www-form-urlencoded)
 *     -> { access_token, token_type: "bearer", user: { sub, email, is_superadmin } }
 *   GET  /api/auth/me       -> {
 *           id, email, is_superadmin, created_at,
 *           tenant_id, sites: [{ id, slug, company_name }]
 *         }
 *   POST /api/auth/register (JSON UserRegister) -> same shape as login
 */
import { apiFetch, setToken, clearToken } from './client';

/**
 * Authenticate with email + password.
 * Backend expects OAuth2PasswordRequestForm — form-urlencoded with `username` field.
 * On success, persists the access token and returns the /me user payload.
 */
export async function login(email, password) {
  const body = new URLSearchParams();
  body.set('username', email);
  body.set('password', password);

  const data = await apiFetch('/auth/login', {
    method: 'POST',
    body,
  });

  if (!data || !data.access_token) {
    throw new Error('Unexpected login response');
  }
  setToken(data.access_token);

  // Fetch the canonical user record so role/created_at are populated.
  try {
    return await me();
  } catch {
    // fall back to the embedded user object if /me briefly fails
    return normalizeUser(data.user);
  }
}

/** Clear the stored token. Does NOT call a backend endpoint — backend is stateless JWT. */
export function logout() {
  clearToken();
}

/**
 * Fetch the current user. Uses `skipAuthRedirect` because callers (AuthContext on mount)
 * need to differentiate "no session" from "hard redirect" gracefully.
 */
export async function me() {
  const raw = await apiFetch('/auth/me', { skipAuthRedirect: true });
  return normalizeUser(raw);
}

function normalizeUser(raw) {
  if (!raw) return null;
  const isAdmin = Boolean(raw.is_superadmin ?? raw.isSuperadmin ?? raw.admin);
  // sites/tenant_id are only present on /me responses (not on the embedded
  // user object returned by /login), so login→me is still the canonical
  // path. We still accept either casing defensively.
  const rawSites = raw.sites ?? raw.Sites ?? [];
  const sites = Array.isArray(rawSites)
    ? rawSites.map((s) => ({
        id: s.id ?? null,
        slug: s.slug ?? null,
        companyName: s.company_name ?? s.companyName ?? null,
      }))
    : [];
  return {
    id: raw.id ?? raw.sub ?? null,
    email: raw.email ?? null,
    isSuperadmin: isAdmin,
    role: isAdmin ? 'admin' : 'user',
    createdAt: raw.created_at ?? raw.createdAt ?? null,
    tenantId: raw.tenant_id ?? raw.tenantId ?? null,
    sites,
    raw,
  };
}
